import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { FormArray, FormBuilder, Validators } from '@angular/forms';
import { ClockHour, Company, Feature, FeatureUser } from '@tacliatech/types';
import { hours, minutes } from './time-input-objects';
import { Subscription } from 'rxjs';
import { CompanyModuleService } from '../../services/company';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'roma-add-break',
  templateUrl: './add-break.component.html',
  styleUrls: ['./add-break.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddBreakComponent {
  @Input()
  mode: 'ADD' | 'EDIT' = 'ADD';

  @Input()
  set data(value: ClockHour.Histories) {
    this._data = value;
    this.patchParams();
  }

  get data() {
    return this._data;
  }

  @Input()
  showButton = true;

  @Input()
  disabled = false;

  @Output()
  changes = new EventEmitter<ClockHour.Breaks>();

  breaks: ClockHour.Breaks = [];

  private sub$ = new Subscription();

  idActiveModules$ = this.companyModuleService.idActiveModules$;
  idModuleRef = Company.IdModule;

  featureRef = Feature;
  featureRefUser = FeatureUser;

  initialStateForm = () =>
    this.fb.group({
      dates: this.fb.array([]),
    });

  initialStateFormWithOneElement = () =>
    this.fb.group({
      dates: this.fb.array([
        this.fb.group({
          startDate: ['', Validators.required],
          endDate: ['', Validators.required],
          duration: [{}],
          startHour: [{}],
          endHour: [{}],
          reason: '',
        }),
      ]),
    });

  form = this.initialStateForm();

  editMode = {
    index: null,
    active: false,
  };

  hour = '01';
  min = '00';

  hours = hours;
  minutes = minutes;

  startTimePicker = null;
  endTimePicker = null;

  private _data: ClockHour.Breaks = [];

  constructor(
    private fb: FormBuilder,
    private changeDetectionRef: ChangeDetectorRef,
    private companyModuleService: CompanyModuleService,
    private i18n: TranslateService
  ) {}

  get isMobile() {
    return window.innerWidth < 768;
  }

  hourChange(hour) {
    this.hour = hour;
  }

  minutesChange(min) {
    this.min = min;
  }

  resetTimePlaceholder() {
    this.hour = '01';
    this.min = '00';
  }

  openTimePicker(i, input) {
    const arrayControl = this.form.controls['dates'] as FormArray;
    this.resetTimePlaceholder();
    if (input === 'startDate') {
      this.startTimePicker = i;
      this.endTimePicker = null;
      if (arrayControl.at(i).value.startDate != '') {
        this.convertTimeValues(arrayControl.at(i).value.startDate);
      }
    }

    if (input === 'endDate') {
      this.endTimePicker = i;
      this.startTimePicker = null;
      if (arrayControl.at(i).value.endDate != '') {
        this.convertTimeValues(arrayControl.at(i).value.endDate);
      }
    }
  }

  setTime(i, input) {
    const selectedTime = this.hour + ':' + this.min;
    const langArr = <FormArray>this.form.controls['dates'];

    if (input === 'startDate') {
      langArr.controls[i].patchValue({ startDate: selectedTime });
      this.startTimePicker = null;
      this.onChangeEvent(i);
    } else {
      langArr.controls[i].patchValue({ endDate: selectedTime });
      this.endTimePicker = null;
      this.onChangeEvent(i);
    }
  }

  convertTimeValues(time) {
    const ts = time.toString();
    this.hour = ts.substr(0, 2);
    this.min = ts.substr(3, 2);
  }

  onChangeEvent(index: number) {
    const arrayControl = this.form.controls['dates'] as FormArray;
    if (
      arrayControl.at(index).value.startDate &&
      arrayControl.at(index).value.endDate
    ) {
      if (this.breaks.length > index) {
        this.editMode.index = index;
        this.editMode.active = true;
        this.draw();
        this.handleEdit(index);
      } else {
        this.handleAdd(index);
      }

      this.replaceHistoriesEndHour();
      this.draw();
    }
  }

  get dates() {
    return this.form.controls['dates'] as FormArray;
  }

  addDate() {
    const datesForm = this.fb.group({
      startDate: ['', Validators.required],
      endDate: ['', Validators.required],
      duration: [{}],
      startHour: [{}],
      endHour: [{}],
      reason: '',
    });
    this.dates.push(datesForm);
    this.draw();
  }

  private handleAdd(i) {
    const history: ClockHour.Break = this.getBreakData(i);

    this.breaks = [
      ...this.breaks,
      {
        ...history,
      },
    ];
  }

  private handleEdit(i) {
    const editedBreak: ClockHour.Break = this.getBreakData(i);
    this.breaks = this.breaks.map((history, index) => {
      return this.editMode.index === index
        ? {
            ...history,
            ...editedBreak,
          }
        : history;
    });
    this.resetEditMode();
  }

  private getBreakData(i: number) {
    const arrayControl = this.form.controls['dates'] as FormArray;

    const startHour: ClockHour.Hour = ClockHour.fromStringHourToObjectHour(
      arrayControl.at(i).value.startDate
    );
    const endHour: ClockHour.Hour = ClockHour.fromStringHourToObjectHour(
      arrayControl.at(i).value.endDate
    );

    const startDate = arrayControl.at(i).value.startDate as string;
    const endDate = arrayControl.at(i).value.endDate as string;

    const duration: ClockHour.Hour = ClockHour.getDifferenceHours(
      endDate,
      startDate
    );

    const history: ClockHour.Break = {
      startDate,
      endDate,
      duration,
      startHour,
      endHour,
      reason: arrayControl.at(i).value.reason,
    };
    return history;
  }

  private resetEditMode() {
    this.editMode.index = null;
    this.editMode.active = false;
    this.draw();
  }

  delete(index: number) {
    this.breaks = this.breaks.filter((_, i) => index !== i);
    this.dates.clear();
    this.replaceHistoriesEndHour();
    this.resetEditMode();
  }

  private patchParams() {
    this.breaks = [...this.data];

    if (this.breaks.length) {
      this.form = this.initialStateForm();

      for (let index = 0; this.breaks.length > index; index++) {
        const datesForm = this.fb.group({
          startDate: [this.breaks[index].startDate, Validators.required],
          endDate: [this.breaks[index].endDate, Validators.required],
          duration: [this.breaks[index].duration],
          startHour: [this.breaks[index].startHour],
          endHour: [this.breaks[index].endHour],
          reason: this.breaks[index].reason || '',
        });
        this.dates.push(datesForm);
      }
    } else {
      this.form = this.initialStateFormWithOneElement();
    }
    this.draw();
  }

  changeReason(i: number) {
    const arrayControl = this.form.controls['dates'] as FormArray;
    const reason = arrayControl.at(i).value.reason;

    if (this.breaks[i]) {
      this.breaks[i].reason = reason;
    }
    this.emitChanges();
  }

  emitChanges() {
    this.changes.emit(this.breaks);
    this.draw();
  }

  private replaceHistoriesEndHour() {
    this.breaks = this.breaks.map((history) => {
      const endHour: ClockHour.Hour = ClockHour.fromStringHourToObjectHour(
        history.endDate
      );
      return {
        ...history,
        endHour,
      };
    });

    this.emitChanges();
  }

  private draw() {
    this.changeDetectionRef.detectChanges();
  }
}
